import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import InstallAppDialog from './InstallAppDialog';
import { useLocation } from 'react-router-dom';
import MensalidadeAbertaDialog from './MensalidadeAbertaDialog';
import { useAuth } from '../AuthContext';
import { avisoMensalidade, concluirAvisoMensalidade } from '../utils/avisoMensalidade';
import { ativarPush, estadoPush, marcarPushRecusado, pushRecusadoRecentemente } from '../utils/pushNativo';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import { biometriaDisponivel, situacaoLocal, marcarLocal, cadastrarBiometria } from '../utils/biometria';
import { assinar, podeInstalar, ehCelular } from '../utils/pwa';
import tokens from '../theme/tokens';

const CHAVE_INSTALACAO = 'rfc_instalar_recusado_em';
const ESPERA_INSTALACAO = 14 * 24 * 3600 * 1000; // volta a perguntar em 14 dias
let perguntouNestaSessao = false;
let perguntouBiometria = false;
let avisoMensalidadeAberto = false;
let perguntouPush = false;

function instalacaoRecusadaRecentemente() {
  const em = Number(localStorage.getItem(CHAVE_INSTALACAO) || 0);
  return em && Date.now() - em < ESPERA_INSTALACAO;
}

/**
 * Avisos exibidos uma vez por sessão, um de cada vez (renderizado uma vez, fora das páginas,
 * para não sumir quando o app redireciona após o login):
 * 1) mensalidade em aberto com pagamento PIX (o redirecionamento para a Ordem de chegada espera ele fechar);
 * 2) oferecer login por biometria após entrar com senha (aparelhos com Face ID/Touch ID/digital);
 * 3) convidar para ativar as notificações (push nativo) neste aparelho;
 * 4) convidar para instalar o sistema como app no celular.
 */
const AppPrompts = () => {
  const { isAuthenticated, userLogado, token } = useAuth();
  const { pathname } = useLocation();
  const [mostrarBiometria, setMostrarBiometria] = useState(false);
  const [mostrarInstalar, setMostrarInstalar] = useState(false);
  const [mostrarPush, setMostrarPush] = useState(false);
  const [ativando, setAtivando] = useState(false);
  const [toast, setToast] = useState(null);
  const [mensalidades, setMensalidades] = useState([]); // cobranças pendentes para o aviso de entrada
  const userId = userLogado?._id;
  avisoMensalidadeAberto = mensalidades.length > 0;
  // Com senha temporária, os avisos esperam o jogador criar a própria senha (TrocarSenhaObrigatoria)
  const ehJogador = isAuthenticated && userId && !['Arbitro', 'Goleiro'].includes(userLogado?.perfil) && !userLogado?.senhaTemporaria;

  const oferecerInstalacao = () => {
    if (perguntouNestaSessao || !ehCelular() || !podeInstalar() || instalacaoRecusadaRecentemente()) return;
    perguntouNestaSessao = true;
    setMostrarInstalar(true);
  };

  // Convite para ativar as notificações neste aparelho (a permissão do navegador só é pedida no toque)
  const oferecerNotificacoesOuInstalacao = async (ativo = () => true) => {
    if (ehJogador && !perguntouPush && !pushRecusadoRecentemente() && (await estadoPush()) === 'inativo') {
      perguntouPush = true;
      if (ativo()) setMostrarPush(true);
    } else if (ativo()) {
      oferecerInstalacao();
    }
  };

  // Depois do aviso de mensalidade (ou sem ele): biometria, notificações e, por fim, instalação do app
  const seguirAvisos = async (ativo = () => true) => {
    if (ehJogador && !perguntouBiometria && !situacaoLocal(userId) && (await biometriaDisponivel())) {
      perguntouBiometria = true;
      if (ativo()) setMostrarBiometria(true);
    } else if (ativo()) {
      oferecerNotificacoesOuInstalacao(ativo);
    }
  };

  const fecharPush = (recusou) => {
    if (recusou) marcarPushRecusado();
    setMostrarPush(false);
    setTimeout(oferecerInstalacao, 800);
  };

  const ativarNotificacoes = async () => {
    setAtivando(true);
    const resultado = await ativarPush();
    setAtivando(false);
    setToast(resultado.ok ? { severity: 'success', message: 'Notificações ativadas! Você vai receber os avisos da pelada neste aparelho.' } : { severity: 'error', message: resultado.message });
    fecharPush(!resultado.ok);
  };

  useEffect(() => {
    let ativo = true;
    // A verificação da mensalidade começa na hora (o redirecionamento pós-login depende dela);
    // os demais avisos esperam um pouco para não aparecerem junto com a tela carregando.
    const verificacao = ehJogador ? avisoMensalidade(userId, token).pendentes : Promise.resolve([]);
    const timer = setTimeout(async () => {
      const pendentes = await verificacao;
      if (!ativo) return;
      if (pendentes.length > 0) setMensalidades(pendentes);
      else seguirAvisos(() => ativo);
    }, 800);
    const cancelar = assinar(() => {
      if (!mostrarBiometria && !avisoMensalidadeAberto) oferecerInstalacao();
    });
    return () => {
      ativo = false;
      clearTimeout(timer);
      cancelar();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehJogador, userId]);

  const fecharBiometria = (recusou) => {
    if (recusou) marcarLocal(userId, 'recusada');
    setMostrarBiometria(false);
    setTimeout(() => oferecerNotificacoesOuInstalacao(), 800);
  };

  const ativar = async () => {
    setAtivando(true);
    const resultado = await cadastrarBiometria(userId);
    setAtivando(false);
    setToast(resultado.ok ? { severity: 'success', message: 'Biometria ativada! Na próxima vez, toque em "Entrar com biometria".' } : { severity: 'error', message: resultado.message });
    if (resultado.ok) fecharBiometria(false);
  };

  return (
    <>
      <MensalidadeAbertaDialog
        open={mensalidades.length > 0 && !pathname.startsWith('/pagamento')}
        pendentes={mensalidades}
        onClose={() => {
          setMensalidades([]);
          concluirAvisoMensalidade(); // libera o redirecionamento para a Ordem de chegada
          setTimeout(() => seguirAvisos(), 800);
        }}
      />

      <Dialog open={mostrarBiometria} onClose={() => fecharBiometria(true)} fullWidth maxWidth="xs">
        <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
          <Box sx={{ width: 72, height: 72, mx: 'auto', borderRadius: '50%', bgcolor: tokens.color.brandSoft, color: tokens.color.brand, display: 'grid', placeItems: 'center', mb: 2 }}>
            <FingerprintRoundedIcon sx={{ fontSize: 44 }} />
          </Box>
          <Typography variant="h6">Entrar com biometria?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Use o Face ID, Touch ID ou a digital deste aparelho para entrar sem digitar a senha. Sua biometria não sai do celular.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => fecharBiometria(true)} color="inherit">Agora não</Button>
          <Button variant="contained" startIcon={<FingerprintRoundedIcon />} onClick={ativar} disabled={ativando}>Ativar biometria</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mostrarPush} onClose={() => fecharPush(true)} fullWidth maxWidth="xs">
        <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
          <Box sx={{ width: 72, height: 72, mx: 'auto', borderRadius: '50%', bgcolor: tokens.color.brandSoft, color: tokens.color.brand, display: 'grid', placeItems: 'center', mb: 2 }}>
            <NotificationsActiveRoundedIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6">Receber os avisos da pelada?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Pelada aberta, avaliação liberada, mensalidades e outros avisos chegam neste aparelho, mesmo com o app fechado.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => fecharPush(true)} color="inherit">Agora não</Button>
          <Button variant="contained" startIcon={<NotificationsActiveRoundedIcon />} onClick={ativarNotificacoes} disabled={ativando}>Ativar notificações</Button>
        </DialogActions>
      </Dialog>

      <InstallAppDialog
        open={mostrarInstalar}
        onClose={(instalou) => {
          setMostrarInstalar(false);
          if (instalou) setToast({ severity: 'success', message: 'App instalado! Procure o ícone do Riviera FC na sua tela inicial.' });
        }}
        onRecusar={() => {
          localStorage.setItem(CHAVE_INSTALACAO, String(Date.now()));
          setMostrarInstalar(false);
        }}
      />

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert variant="filled" severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </>
  );
};

export default AppPrompts;
